import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ open, onClose }) => {
  const { signInWithEmail, verifyEmailOtp, loading } = useAuth();
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset to email step whenever modal is closed/reopened
  useEffect(() => {
    if (!open) {
      setStep('email');
      setEmail('');
      setOtpCode('');
      setErrorMessage(null);
      setPendingEmail('');
    }
  }, [open]);

  if (!open) return null;

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage(null);

    const result = await signInWithEmail(email.trim());

    if (result.error) {
      setErrorMessage(result.error);
    } else {
      setPendingEmail(email.trim());
      setEmail('');
      setStep('otp');
    }

    setSubmitting(false);
  };

  const handleVerifyOtp = async () => {
    setSubmitting(true);
    setErrorMessage(null);

    const result = await verifyEmailOtp(pendingEmail, otpCode.trim());

    if (result.error) {
      setErrorMessage('Invalid or expired code. Try again or request a new one.');
    } else {
      onClose();
    }

    setSubmitting(false);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(31, 76, 64, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '90%',
          maxWidth: '420px',
          backgroundColor: 'var(--color-surface-white)',
          borderRadius: '20px',
          padding: '28px',
          boxShadow: '0 20px 50px rgba(31, 76, 64, 0.2)',
          position: 'relative'
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'transparent',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            color: 'var(--color-text-muted)'
          }}
          aria-label="Close"
        >
          ✕
        </button>

        <h2 style={{ margin: '0 0 20px 0', color: 'var(--color-primary)' }}>
          {step === 'email' ? 'Sign In' : 'Check your email'}
        </h2>

        {/* Step 1 — Email */}
        {step === 'email' && (
          <form onSubmit={handleSendEmail}>
            <label
              htmlFor="auth-email"
              style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}
            >
              Email
            </label>
            <input
              id="auth-email"
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid var(--color-border)',
                marginBottom: '12px',
                fontSize: '16px',
                color: 'var(--color-text)',
                backgroundColor: 'var(--color-surface-white)'
              }}
            />
            <button
              type="submit"
              disabled={submitting || loading}
              className="btn-primary"
              style={{
                width: '100%',
                marginBottom: '15px',
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? 'Sending...' : 'Send Code'}
            </button>
          </form>
        )}

        {/* Step 2 — OTP code */}
        {step === 'otp' && (
          <div>
            <p style={{ fontSize: 14, color: 'var(--color-text-muted)', marginTop: 0, marginBottom: 20 }}>
              We sent a 6-digit code to <strong>{pendingEmail}</strong>. Enter it below to sign in.
            </p>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="123456"
              value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: 28,
                textAlign: 'center',
                letterSpacing: '0.35em',
                borderRadius: 10,
                border: '1px solid var(--color-border)',
                marginBottom: 12,
                color: 'var(--color-text)',
                backgroundColor: 'var(--color-surface-white)',
                fontVariantNumeric: 'tabular-nums'
              }}
              autoFocus
              autoComplete="one-time-code"
            />
            <button
              onClick={handleVerifyOtp}
              disabled={otpCode.length < 6 || submitting}
              className="btn-primary"
              style={{
                width: '100%',
                marginBottom: 12,
                cursor: otpCode.length < 6 || submitting ? 'not-allowed' : 'pointer',
                opacity: otpCode.length < 6 ? 0.6 : 1
              }}
            >
              {submitting ? 'Verifying...' : 'Sign In'}
            </button>
            <button
              onClick={() => { setStep('email'); setOtpCode(''); setErrorMessage(null); }}
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                color: 'var(--color-text-muted)',
                fontSize: 13,
                cursor: 'pointer',
                padding: '4px 0'
              }}
            >
              ← Use a different email
            </button>
          </div>
        )}

        {errorMessage && (
          <div
            style={{
              marginTop: '15px',
              padding: '10px',
              borderRadius: '10px',
              backgroundColor: 'var(--color-danger-light)',
              color: 'var(--color-danger-text)',
              border: '1px solid var(--color-danger)',
              fontSize: '14px'
            }}
          >
            {errorMessage}
          </div>
        )}

        <div style={{ marginTop: '20px', fontSize: '13px', color: 'var(--color-text-muted)' }}>
          {step === 'email'
            ? "We'll email you a code. Check your spam folder if you don't see it."
            : 'The code expires in 1 hour. The link in the email also works on desktop.'}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
